import React, { useMemo, useState } from 'react';
import { useWindowDimensions } from 'react-native';
import { Button, Col, Row, Select, Text, modal } from 'components';
import { SHIPPING_SERVICES } from 'const';
import { TCMSOrder } from 'type';
import OrderTable from 'components/elements/table/OrderTablePro';
import { ColumnsType } from 'antd/es/table';
import { getDefaultShippingService, getShopifyShippingMethodLabel, isOrderPaid } from '../utils';

type TConfirmResult = {
  confirmed: boolean;
  shippingServices?: Record<string, string>;
};

const ConfirmShippingMethodModal = ({
  orders,
  onConfirm,
  onCancel,
}: {
  orders: TCMSOrder[];
  onConfirm: (shippingServices: Record<string, string>) => void;
  onCancel: () => void;
}) => {
  const { height: screenHeight } = useWindowDimensions();
  const [shippingServices, setShippingServices] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    orders.forEach((order) => {
      initial[String(order['Order ID'])] = getDefaultShippingService(order);
    });
    return initial;
  });

  const hasPaidOrder = useMemo(() => orders.some(isOrderPaid), [orders]);

  const tableData = useMemo(() => orders.map((order) => ({
    orderNo: order['Order Number'],
    orderId: order['Order ID'],
    shopifyShipping: getShopifyShippingMethodLabel(order),
    order,
  })), [orders]);

  const columns: ColumnsType<any> = [
    {
      title: 'Order No',
      key: 'orderNo',
      dataIndex: 'orderNo',
      render: (value) => `#${value}`,
      align: 'center',
      width: '15%',
    },
    {
      title: 'Shopify Shipping',
      key: 'shopifyShipping',
      dataIndex: 'shopifyShipping',
      align: 'center',
      render: (value) => value || 'Not available',
    },
    {
      title: 'Change Shipping',
      key: 'shippingService',
      dataIndex: 'orderId',
      align: 'center',
      width: '30%',
      render: (orderId, record) => {
        const order = record.order as TCMSOrder;
        const shippingAddress = order?.['Raw Data']?.shipping_address;
        const isUK = shippingAddress?.country === 'United Kingdom';
        const isGlobal = !isUK;
        const isITLuggage = order['Store ID'] === '123415927657' || order['Client ID'] === '222415927656';
        const shippingLabel = (svc: string) => (isITLuggage && svc === 'DPD' ? 'DPD' : (SHIPPING_SERVICES[svc]?.name ?? svc));
        const id = String(orderId);
        const paid = isOrderPaid(order);
        const serviceOptions = Object.keys(SHIPPING_SERVICES)
          .filter((v) => (isGlobal ? v === 'DPD' : true))
          .map((v) => {
            const service = SHIPPING_SERVICES[v];
            const price = isGlobal ? service.priceGlobal : service.price;
            const name = isITLuggage && v === 'DPD' ? 'DPD' : service.name;
            return {
              label: `${name} - £${price}`,
              value: v,
            };
          });

        return (
          <Select
            value={{
              value: shippingServices[id],
              label: shippingLabel(shippingServices[id] || ''),
            }}
            onChange={(item) => {
              setShippingServices((prev) => ({ ...prev, [id]: item.value }));
            }}
            options={serviceOptions}
            innerProps={{ isDisabled: paid }}
          />
        );
      },
    },
  ];

  return (
    <Col
      width={620}
      bgWhite
      round1
      shadow
      maxHeight={screenHeight - 24}
      borderColor="rgb(16, 24, 64)"
      borderWidth="2px"
    >
      <Col flex1 mv2>
        <Text mh2 bold mb1 fontSize={18} textAlign="center">Confirm shipping method</Text>
        <Text mh2 mb2 fontSize={15} textAlign="center" lineHeight={20} color="rgba(34, 51, 99, 1)">
          Please verify the Shopify shipping method and confirm the shipping service before marking {orders.length === 1 ? 'this order' : 'these orders'} in production.
          {hasPaidOrder ? ' Shipping cannot be changed for orders that have already been paid.' : ''}
        </Text>
        <Col mv1>
          <OrderTable
            style={{
              maxHeight: 320,
              overflowY: 'auto',
            }}
            data={tableData}
            className="payList"
            rowClassName="stripeInvoiceTable"
            columns={columns}
            rowKey={(record) => String(record.orderId)}
            sticky
          />
        </Col>
        <Text mv0 center bold>Do you wish to continue?</Text>
        <Row mh2 marginTop={12}>
          <Col flex1 marginRight={10}>
            <Button
              outline
              height={30}
              width="100%"
              text="Cancel"
              onPress={onCancel}
            />
          </Col>
          <Col flex1>
            <Button
              solid
              height={30}
              width="100%"
              text="Confirm"
              onPress={() => onConfirm(shippingServices)}
            />
          </Col>
        </Row>
      </Col>
    </Col>
  );
};

export const showConfirmShippingMethod = (orders: TCMSOrder[]) => new Promise<TConfirmResult>((resolve) => {
  const handleClose = (result: TConfirmResult) => {
    modal.hide();
    resolve(result);
  };

  modal.show(
    <ConfirmShippingMethodModal
      orders={orders}
      onConfirm={(shippingServices) => handleClose({ confirmed: true, shippingServices })}
      onCancel={() => handleClose({ confirmed: false })}
    />
  );
});
